import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import {
  TrendingUp, Stethoscope, Building2, FileText,
  ChevronRight, Loader2, Search, ArrowLeft, GitCompare, Check,
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { useTheme } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  approved: "#16A34A",
  denied: "#DC2626",
  pending: "#D97706",
  submitted: "#0EA5E9",
  draft: "#6B7280",
};

function ProviderListView({ onSelect }) {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [compareSet, setCompareSet] = useState(new Set());

  useEffect(() => {
    axios.get(`${API}/analytics/providers`, { withCredentials: true })
      .then(res => setProviders(res.data.providers || []))
      .catch(() => toast.error("Failed to load providers"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return providers;
    return providers.filter(p =>
      (p.npi || "").toLowerCase().includes(q) ||
      (p.name || "").toLowerCase().includes(q) ||
      (p.specialty || "").toLowerCase().includes(q) ||
      (p.tax_id || "").toLowerCase().includes(q)
    );
  }, [providers, search]);

  return (
    <Card className={isDark ? "bg-slate-900/50 border-slate-800" : "bg-white border-gray-200"}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className={`flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
            <Stethoscope className="w-5 h-5" />
            Provider Performance — Select an NPI
          </span>
          <div className="flex items-center gap-2">
            {compareSet.size > 0 && (
              <Button
                size="sm"
                onClick={() => navigate(`/provider-compare?npis=${Array.from(compareSet).join(",")}`)}
                className={isDark ? "bg-emerald-500 hover:bg-emerald-400 text-slate-900" : "bg-[#0066a1] hover:bg-[#005080] text-white"}
                data-testid="compare-selected-btn"
              >
                <GitCompare className="w-4 h-4 mr-1" /> Compare ({compareSet.size})
              </Button>
            )}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search NPI, name, specialty, Tax ID..."
                className={`pl-9 w-72 ${isDark ? "bg-slate-800 border-slate-700 text-white" : ""}`}
                data-testid="provider-search-input"
              />
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <p className={`text-sm py-6 text-center ${isDark ? "text-slate-400" : "text-gray-500"}`}>
            No providers found. Submit a few PA requests to populate this dashboard.
          </p>
        ) : (
          <div className="space-y-2">
            {filtered.map(p => {
              const inCompare = compareSet.has(p.npi);
              return (
              <div
                key={p.npi}
                data-testid={`provider-row-${p.npi}`}
                className={`w-full p-4 rounded-lg border transition flex items-center justify-between
                  ${isDark ? "border-slate-700 hover:border-emerald-500/40 bg-slate-800/50 hover:bg-slate-800"
                           : "border-gray-200 hover:border-teal-500/40 bg-white hover:bg-gray-50"}`}
              >
                <button
                  onClick={() => onSelect(p.npi)}
                  className="flex items-center gap-4 flex-1 text-left"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? "bg-slate-700" : "bg-gray-100"}`}>
                    <Stethoscope className={`w-5 h-5 ${isDark ? "text-emerald-400" : "text-teal-600"}`} />
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                      {p.name || "Unknown Provider"} <span className={`font-mono text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>· NPI {p.npi}</span>
                    </p>
                    <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                      {p.specialty || "Specialty —"} {p.tax_id && <>· Tax ID {p.tax_id}</>}
                    </p>
                  </div>
                </button>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>{p.total_pas} PAs</p>
                    <p className="text-sm font-bold">
                      {p.approval_rate !== null && p.approval_rate !== undefined
                        ? <span className={p.approval_rate >= 80 ? "text-green-500" : p.approval_rate >= 50 ? "text-amber-500" : "text-red-500"}>{p.approval_rate.toFixed(0)}%</span>
                        : <span className={isDark ? "text-slate-500" : "text-gray-400"}>No data</span>}
                    </p>
                  </div>
                  <Button
                    variant={inCompare ? "default" : "outline"}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCompareSet(prev => {
                        const next = new Set(prev);
                        if (next.has(p.npi)) next.delete(p.npi);
                        else if (next.size >= 6) { toast.warning("Max 6 providers"); return prev; }
                        else next.add(p.npi);
                        return next;
                      });
                    }}
                    data-testid={`compare-toggle-${p.npi}`}
                    className={inCompare ? (isDark ? "bg-emerald-500 text-slate-900" : "bg-[#0066a1] text-white") : ""}
                  >
                    {inCompare ? <Check className="w-4 h-4" /> : <GitCompare className="w-4 h-4" />}
                  </Button>
                  <ChevronRight className={`w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                </div>
              </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProviderDetailView({ npi, onBack }) {
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/analytics/provider/${encodeURIComponent(npi)}`, {
      params: year ? { year } : {},
      withCredentials: true,
    })
      .then(res => setData(res.data))
      .catch(() => toast.error("Failed to load provider analytics"))
      .finally(() => setLoading(false));
  }, [npi, year]);

  if (loading || !data) {
    return <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  const profile = data.profile;
  const summary = data.summary;
  const cardCls = isDark ? "bg-slate-900/50 border-slate-800" : "bg-white border-gray-200";
  const muted = isDark ? "text-slate-400" : "text-gray-500";
  const text = isDark ? "text-white" : "text-gray-900";

  return (
    <div className="space-y-6">
      {/* Profile header */}
      <Card className={cardCls}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button onClick={onBack} variant="ghost" size="sm" className={muted} data-testid="back-to-list-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div>
                <h2 className={`text-xl font-bold ${text}`}>{profile.name || "Unknown Provider"}</h2>
                <p className={`text-xs ${muted}`}>{profile.specialty || "Specialty —"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={year ? String(year) : "all"} onValueChange={v => setYear(v === "all" ? "" : v)}>
                <SelectTrigger className={`w-36 ${isDark ? "bg-slate-800 border-slate-700 text-white" : ""}`} data-testid="year-filter">
                  <SelectValue placeholder="All time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All time</SelectItem>
                  {(data.available_years || []).map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className={muted}>NPI #:</span> <strong className={text}>{profile.npi}</strong></div>
            <div><span className={muted}>Tax ID / EIN:</span> <strong className={text}>{profile.tax_id || "—"}</strong></div>
            <div><span className={muted}>Facility:</span> <strong className={text}>{profile.facility_id || "—"}</strong></div>
            <div><span className={muted}>Facility NPI:</span> <strong className={text}>{profile.facility_npi || "—"}</strong></div>
            {profile.address && <div className="col-span-2 md:col-span-4"><span className={muted}>Address:</span> <strong className={text}>{profile.address}</strong></div>}
          </div>
        </CardContent>
      </Card>

      {/* Summary KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { k: "Total PAs", v: summary.total_pas, color: isDark ? "text-cyan-400" : "text-teal-600" },
          { k: "Approved", v: summary.approved, color: "text-green-500" },
          { k: "Denied", v: summary.denied, color: "text-red-500" },
          { k: "Pending", v: summary.pending + (summary.submitted || 0), color: "text-amber-500" },
          { k: "Approval Rate", v: summary.approval_rate !== null ? `${summary.approval_rate}%` : "—",
            color: summary.approval_rate >= 80 ? "text-green-500" : summary.approval_rate >= 50 ? "text-amber-500" : "text-red-500" },
        ].map(({ k, v, color }) => (
          <Card key={k} className={cardCls}>
            <CardContent className="p-4">
              <p className={`text-xs uppercase tracking-wider ${muted}`}>{k}</p>
              <p className={`text-2xl font-bold mt-1 ${color}`} data-testid={`kpi-${k.toLowerCase().replace(/\s+/g, '-')}`}>{v}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Year-over-year trend */}
      <Card className={cardCls}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${text}`}>
            <TrendingUp className="w-5 h-5" /> Approval Rate — Year-Over-Year Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.yoy_trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#E5E7EB"} />
              <XAxis dataKey="year" stroke={isDark ? "#94A3B8" : "#6B7280"} />
              <YAxis stroke={isDark ? "#94A3B8" : "#6B7280"} domain={[0, 100]} unit="%" />
              <Tooltip
                contentStyle={isDark ? { background: "#0F172A", border: "1px solid #334155", color: "#fff" } : {}}
                formatter={(v, n) => n === "approval_rate" ? [`${v?.toFixed(1)}%`, "Approval rate"] : [v, n]}
              />
              <Legend />
              <Line type="monotone" dataKey="approval_rate" stroke={isDark ? "#22D3EE" : "#0D9488"} strokeWidth={3} dot={{ r: 5 }} name="Approval %" />
              <Line type="monotone" dataKey="total_pas" stroke={isDark ? "#F59E0B" : "#D97706"} strokeWidth={2} dot={{ r: 4 }} name="Total PAs" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* By Payer breakdown */}
      <Card className={cardCls}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${text}`}>
            <Building2 className="w-5 h-5" /> Approval Rate by Payer {year ? `(${year})` : "(All time)"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_payer.filter(b => b.approval_rate !== null)} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#E5E7EB"} />
              <XAxis dataKey="payer" stroke={isDark ? "#94A3B8" : "#6B7280"} />
              <YAxis stroke={isDark ? "#94A3B8" : "#6B7280"} domain={[0, 100]} unit="%" />
              <Tooltip
                contentStyle={isDark ? { background: "#0F172A", border: "1px solid #334155", color: "#fff" } : {}}
                formatter={(v) => `${v?.toFixed(1)}%`}
              />
              <Bar dataKey="approval_rate" fill={isDark ? "#22D3EE" : "#0D9488"} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

          {/* Detail table */}
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={isDark ? "text-slate-400 border-b border-slate-700" : "text-gray-500 border-b border-gray-200"}>
                  <th className="text-left py-2 px-2">Payer</th>
                  <th className="text-left py-2 px-2">Contract ID</th>
                  <th className="text-right py-2 px-2">Total</th>
                  <th className="text-right py-2 px-2">Approved</th>
                  <th className="text-right py-2 px-2">Denied</th>
                  <th className="text-right py-2 px-2">Pending</th>
                  <th className="text-right py-2 px-2">Rate</th>
                </tr>
              </thead>
              <tbody>
                {data.by_payer.map((p, i) => {
                  const contract = (data.payer_contracts || []).find(c => c.payer === p.payer);
                  return (
                    <tr key={i} className={isDark ? "border-b border-slate-800 hover:bg-slate-800/40" : "border-b border-gray-100 hover:bg-gray-50"} data-testid={`payer-row-${i}`}>
                      <td className="py-2 px-2">
                        <strong className={text}>{p.payer}</strong>
                        {p.payer_id && <span className={`ml-1 text-xs font-mono ${muted}`}>({p.payer_id})</span>}
                      </td>
                      <td className={`py-2 px-2 font-mono text-xs ${muted}`}>{contract?.contract_id || "—"}</td>
                      <td className={`py-2 px-2 text-right ${text}`}>{p.total_pas}</td>
                      <td className="py-2 px-2 text-right text-green-500">{p.approved}</td>
                      <td className="py-2 px-2 text-right text-red-500">{p.denied}</td>
                      <td className="py-2 px-2 text-right text-amber-500">{(p.pending || 0) + (p.submitted || 0)}</td>
                      <td className="py-2 px-2 text-right">
                        {p.approval_rate !== null && p.approval_rate !== undefined ? (
                          <Badge className={
                            p.approval_rate >= 80 ? "bg-green-500/20 text-green-500" :
                            p.approval_rate >= 50 ? "bg-amber-500/20 text-amber-500" :
                            "bg-red-500/20 text-red-500"
                          }>{p.approval_rate.toFixed(1)}%</Badge>
                        ) : <span className={muted}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Payer status mix stacked bar */}
      <Card className={cardCls}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${text}`}>
            <FileText className="w-5 h-5" /> PA Volume & Outcome by Payer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_payer} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#E5E7EB"} />
              <XAxis dataKey="payer" stroke={isDark ? "#94A3B8" : "#6B7280"} />
              <YAxis stroke={isDark ? "#94A3B8" : "#6B7280"} />
              <Tooltip contentStyle={isDark ? { background: "#0F172A", border: "1px solid #334155", color: "#fff" } : {}} />
              <Legend />
              <Bar dataKey="approved" stackId="a" fill={STATUS_COLORS.approved} name="Approved" />
              <Bar dataKey="denied" stackId="a" fill={STATUS_COLORS.denied} name="Denied" />
              <Bar dataKey="pending" stackId="a" fill={STATUS_COLORS.pending} name="Pending" />
              <Bar dataKey="submitted" stackId="a" fill={STATUS_COLORS.submitted} name="Submitted" />
              <Bar dataKey="draft" stackId="a" fill={STATUS_COLORS.draft} name="Draft" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ProviderPerformance() {
  const navigate = useNavigate();
  const [selectedNpi, setSelectedNpi] = useState(null);

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold mb-1">Provider Performance</h1>
          <p className="text-sm text-slate-400">
            Track payer approval rates per provider NPI. Drill in to see year-over-year trends and per-payer breakdowns.
          </p>
        </div>

        {selectedNpi ? (
          <ProviderDetailView npi={selectedNpi} onBack={() => setSelectedNpi(null)} />
        ) : (
          <ProviderListView onSelect={setSelectedNpi} />
        )}
      </div>
    </DashboardLayout>
  );
}
